import {
  ChangeDetectionStrategy, ChangeDetectorRef, Component,
  EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges
} from '@angular/core';
import { MatIcon } from '@angular/material/icon';
import { MatProgressBar } from '@angular/material/progress-bar';
import { FormatEnumPipe } from 'services/format-enum.pipe';
import { EntityActionDefinition } from 'app/API.service.custom';
import formatDistanceToNow from 'date-fns/formatDistanceToNow';
import parseISO from 'date-fns/parseISO';
import format from 'date-fns/format';

export type JobTab = 'running' | 'completed' | 'scheduled';

export interface BulkUploadSummary {
  successfulCount: number;
  duplicateCount: number;
  failedCount: number;
}

@Component({
  selector: 'app-background-jobs-widget',
  standalone: true,
  imports: [MatIcon, MatProgressBar, FormatEnumPipe],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './background-jobs-widget.component.html',
  styleUrls: ['./background-jobs-widget.component.scss']
})
export class BackgroundJobsWidgetComponent implements OnChanges, OnDestroy {
  @Input() jobs: EntityActionDefinition[] = [];
  @Input() uploadSummaries: { [jobId: string]: BulkUploadSummary } = {};
  @Output() dismissed = new EventEmitter<void>();
  @Output() downloadExport = new EventEmitter<EntityActionDefinition>();
  @Output() viewExports = new EventEmitter<void>();
  @Output() viewBulkUpload = new EventEmitter<EntityActionDefinition>();

  activeTab: JobTab = 'running';
  isDismissed = false;
  slidingOut = false;
  pinned = false;
  currentIndex = 0;

  private autoCloseTimer: any = null;
  private lastSeenJobIds = new Set<string>();
  private static readonly IGNORED_ACTIONS = new Set(['LEAD_SOURCING', 'PROSPECT_LEAD_EXPIRY', 'TASK_EXPIRY']);

  private get filteredJobs(): EntityActionDefinition[] {
    return this.jobs.filter(j => !BackgroundJobsWidgetComponent.IGNORED_ACTIONS.has(j.action));
  }

  get running(): EntityActionDefinition[] {
    return this.filteredJobs.filter(j =>
      (j.status === 'REQUESTED' || j.status === 'UNDER_PROCESSING') &&
      !this.isFutureScheduled(j)
    );
  }

  get completed(): EntityActionDefinition[] {
    return this.filteredJobs
      .filter(j => j.status === 'SUCCESS' || j.status === 'FAILURE')
      .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
  }

  get scheduled(): EntityActionDefinition[] {
    return this.filteredJobs.filter(j =>
      j.status === 'REQUESTED' && this.isFutureScheduled(j)
    );
  }

  get activeList(): EntityActionDefinition[] {
    if (this.activeTab === 'running') return this.running;
    if (this.activeTab === 'completed') return this.completed;
    return this.scheduled;
  }

  get currentJob(): EntityActionDefinition | null {
    return this.activeList[this.currentIndex] || null;
  }

  get totalCount(): number {
    return this.running.length + this.completed.length + this.scheduled.length;
  }

  get hasAnyJobs(): boolean {
    return this.totalCount > 0;
  }

  constructor(private cdr: ChangeDetectorRef) {}

  ngOnChanges(changes: SimpleChanges): void {
    // Re-render when uploadSummaries changes (async fetch completes)
    if (changes['uploadSummaries'] && !changes['jobs']) {
      this.cdr.markForCheck();
      return;
    }
    if (!changes['jobs']) return;

    const prev: EntityActionDefinition[] = changes['jobs'].previousValue || [];
    const curr: EntityActionDefinition[] = changes['jobs'].currentValue || [];

    // Detect new jobs or status changes
    const prevMap = new Map(prev.map(j => [j.id, j.status]));
    let hasNewActivity = false;
    for (const job of curr) {
      if (BackgroundJobsWidgetComponent.IGNORED_ACTIONS.has(job.action)) continue;
      const oldStatus = prevMap.get(job.id);
      if (!oldStatus || oldStatus !== job.status) {
        hasNewActivity = true;
        break;
      }
    }

    // Auto-select best tab
    this.autoSelectTab();

    // Clamp index
    this.currentIndex = Math.min(this.currentIndex, Math.max(this.activeList.length - 1, 0));

    // Un-dismiss on new activity
    if (this.isDismissed && hasNewActivity && !changes['jobs'].firstChange) {
      this.isDismissed = false;
      this.startAutoCloseTimer();
    }

    // Start timer if visible
    if (!this.isDismissed && this.hasAnyJobs && hasNewActivity) {
      this.startAutoCloseTimer();
    }

    this.cdr.markForCheck();
  }

  private autoSelectTab(): void {
    // Prioritize: running > completed > scheduled
    if (this.running.length > 0) {
      this.activeTab = 'running';
    } else if (this.completed.length > 0) {
      this.activeTab = 'completed';
    } else if (this.scheduled.length > 0) {
      this.activeTab = 'scheduled';
    }
    this.currentIndex = 0;
  }

  selectTab(tab: JobTab): void {
    this.activeTab = tab;
    this.currentIndex = 0;
    this.cdr.markForCheck();
  }

  prev(): void {
    if (this.currentIndex > 0) {
      this.currentIndex--;
      this.cdr.markForCheck();
    }
  }

  next(): void {
    if (this.currentIndex < this.activeList.length - 1) {
      this.currentIndex++;
      this.cdr.markForCheck();
    }
  }

  dismiss(): void {
    if (this.slidingOut) return;
    this.clearAutoCloseTimer();
    this.slidingOut = true;
    this.cdr.markForCheck();
    setTimeout(() => {
      this.slidingOut = false;
      this.isDismissed = true;
      this.dismissed.emit();
      this.cdr.markForCheck();
    }, 300);
  }

  private autoDismiss(): void {
    if (this.slidingOut) return;
    this.slidingOut = true;
    this.cdr.markForCheck();
    setTimeout(() => {
      this.slidingOut = false;
      this.isDismissed = true;
      this.cdr.markForCheck();
    }, 300);
  }

  togglePin(): void {
    this.pinned = !this.pinned;
    if (this.pinned) {
      this.clearAutoCloseTimer();
    } else {
      this.startAutoCloseTimer();
    }
    this.cdr.markForCheck();
  }

  pauseAutoClose(): void {
    this.clearAutoCloseTimer();
  }

  resumeAutoClose(): void {
    if (!this.pinned) this.startAutoCloseTimer();
  }

  private startAutoCloseTimer(): void {
    if (this.pinned) return;
    this.clearAutoCloseTimer();
    this.autoCloseTimer = setTimeout(() => this.autoDismiss(), 8_000);
  }

  private clearAutoCloseTimer(): void {
    if (this.autoCloseTimer) {
      clearTimeout(this.autoCloseTimer);
      this.autoCloseTimer = null;
    }
  }

  private isFutureScheduled(job: EntityActionDefinition): boolean {
    if (!job.startDateTime) return false;
    return new Date(job.startDateTime) > new Date();
  }

  timeAgo(dateStr: string): string {
    if (!dateStr) return '';
    try {
      return formatDistanceToNow(parseISO(dateStr), { addSuffix: true });
    } catch {
      return '';
    }
  }

  formatScheduledTime(dateStr: string): string {
    if (!dateStr) return '';
    try {
      return format(parseISO(dateStr), 'MMM d, h:mm a');
    } catch {
      return '';
    }
  }

  ngOnDestroy(): void {
    this.clearAutoCloseTimer();
  }
}
